/**
 * assigneeRanking.ts
 * IMP2-2 (audit #2) — rank assignee-picker candidates so the obvious picks
 * come FIRST instead of alphabetical order interleaving people on vacation
 * (whose pick would hard-409, Q3B) and fully-busy people with the free,
 * qualified ones. Pure functions — shared by InlineAssigneePicker and
 * AssignEmployeesDialog so the two staffing surfaces never drift apart.
 *
 * Ranking key (best first):
 *   1. absent on the booking's days → LAST (assigning would 409 — Q3B);
 *   2. free days on the booking's window, DESCENDING;
 *   3. skill level for the task type, DESCENDING (unrated sorts below rated);
 *   4. display name, ascending (stable tie-break).
 *
 * Availability loads ASYNC after the dropdown opens (useAssigneeCapacity).
 * While it is unknown (loading or failed — P-18) the availability keys are
 * SKIPPED entirely: candidates rank by skill + name only, deterministically,
 * instead of treating everyone as free and then re-shuffling on every keystroke.
 * When the load lands the list re-ranks ONCE with the full key.
 *
 * G-1 deferral respected: this is ordering only — the planner still picks.
 */

/** The load fields the ranking needs (structural subset of AssigneeLoad). */
export interface RankableLoad {
  /** Busy working days within the booking's own window. */
  busyDaysOnBookingDays: number;
  /** Working days in the booking's window. */
  windowWorkingDays: number;
  /** True when a dated absence covers one of the booking's days (Q3B). */
  absentOnBookingDays: boolean;
  /**
   * Staffing Allocation % (PR B — D6): Σ weekly allocation %. When present on
   * BOTH compared candidates it is the PRIMARY availability key (least-loaded
   * first) — "suggest who's free". Optional so older callers (and the existing
   * booking-window-only ranking) are unchanged when it's absent.
   */
  allocationPercent?: number;
}

/** The candidate fields the ranking needs (structural subset of AssignableUser). */
export interface RankableUser {
  AppUserKey: string;
  DisplayName: string;
  /** Skill level (1–5) for the task type; null = unrated; undefined = not requested. */
  SkillLevel?: number | null;
}

/** Free working days a candidate has on the booking's window (never negative). */
export function freeDaysOnWindow(load: RankableLoad | null | undefined): number {
  if (!load) return 0;
  return Math.max(0, load.windowWorkingDays - load.busyDaysOnBookingDays);
}

/**
 * Sort candidates for the assignee pickers: available + qualified first.
 * Returns a NEW array (input untouched — memo-friendly).
 *
 * @param users             Candidates (already filtered: not assigned, qualified).
 * @param loadByUserKey     Availability per AppUserKey (useAssigneeCapacity's map).
 *                          A candidate missing from the map ranks as not-absent
 *                          with 0 free days (conservative — below known-free).
 * @param availabilityKnown Pass `!availabilityUnknown`. When false the
 *                          availability keys are skipped (see header).
 */
export function rankAssignees<T extends RankableUser>(
  users: readonly T[],
  loadByUserKey: ReadonlyMap<string, RankableLoad>,
  availabilityKnown: boolean,
): T[] {
  return [...users].sort((a, b) => {
    if (availabilityKnown) {
      const la = loadByUserKey.get(a.AppUserKey) ?? null;
      const lb = loadByUserKey.get(b.AppUserKey) ?? null;
      // 1. absent (would hard-409) sinks to the bottom.
      const absA = la?.absentOnBookingDays ? 1 : 0;
      const absB = lb?.absentOnBookingDays ? 1 : 0;
      if (absA !== absB) return absA - absB;
      // 2. "suggest who's free" (D6): when both carry a weekly allocation %,
      //    the LEAST-loaded person ranks first. Falls through to the
      //    booking-window free-days key when either % is absent (older callers).
      const allocA = la?.allocationPercent;
      const allocB = lb?.allocationPercent;
      if (typeof allocA === 'number' && typeof allocB === 'number') {
        const allocDiff = allocA - allocB;
        if (allocDiff !== 0) return allocDiff;
      }
      // 3. more free days on the booking's window first.
      const freeDiff = freeDaysOnWindow(lb) - freeDaysOnWindow(la);
      if (freeDiff !== 0) return freeDiff;
    }
    // 3. higher skill first (null/undefined = unrated → below any rated level).
    const skillDiff = (b.SkillLevel ?? -1) - (a.SkillLevel ?? -1);
    if (skillDiff !== 0) return skillDiff;
    // 4. name tie-break.
    return a.DisplayName.localeCompare(b.DisplayName);
  });
}

/**
 * True when this candidate's pick is guaranteed to 409 (dated absence on the
 * booking's days, Q3B — no bypass exists). Used to DISABLE the option; the row
 * still renders with its "unavailable (day off)" reason. Never disables while
 * availability is unknown — the save-time gate stays authoritative.
 */
export function isPickBlockedByAbsence(
  load: RankableLoad | null | undefined,
  availabilityKnown: boolean,
): boolean {
  return availabilityKnown && (load?.absentOnBookingDays ?? false);
}
